import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Dropdown, Form, Modal, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBell,
  faBellSlash,
  faCheck,
  faClock,
  faCloudArrowUp,
  faInbox,
  faPen,
  faPlus,
  faRepeat,
  faSnowflake,
  faTrash,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

import api from "../api/API";
import {
  adiarLembrete,
  alterarStatus,
  atualizarLembrete,
  converterEmTarefa,
  listarLembretes,
  removerLembrete,
} from "../api/lembretesApi";
import { useWorkspace } from "../context/WorkspaceContext";
import { useLembretes } from "../context/LembretesContext";
import { garantirSincronizacaoAutomatica } from "../services/captureQueue";
import {
  ativarNotificacoes,
  desativarNotificacoes,
  endpointDesteAparelho,
  enviarNotificacaoTeste,
  garantirInscricaoSincronizada,
  listarDispositivos,
  permissaoAtual,
  precisaInstalarParaNotificar,
  pushSuportado,
  sincronizarTokenComServiceWorker,
} from "../services/pushClient";
import { estaVencido, formatarLembrarEm, paraInputDateTime } from "../utils/reminderParser";
import {
  RECURRENCE_OPTIONS,
  REMINDER_RECURRENCE,
  REMINDER_STATUS,
  ehRecorrente,
  getRecurrenceLabel,
} from "../utils/reminderStatus";
import { TASK_PRIORITY, getTaskPriorityLabel } from "../utils/taskPriority";
import "./LembretesPage.css";

const ABAS = [
  { id: "abertos", rotulo: "Abertos", status: REMINDER_STATUS.ABERTO },
  { id: "feitos", rotulo: "Feitos", status: REMINDER_STATUS.FEITO },
  {
    id: "arquivo",
    rotulo: "Arquivo",
    status: `${REMINDER_STATUS.CONVERTIDO},${REMINDER_STATUS.DESCARTADO}`,
  },
];

const PRIORIDADES = [
  TASK_PRIORITY.URGENT,
  TASK_PRIORITY.HIGH,
  TASK_PRIORITY.MEDIUM,
  TASK_PRIORITY.LOW,
];

/** Opcoes de adiar. `minutos` desloca; `calcular` devolve um horario exato. */
const OPCOES_ADIAR = [
  { id: "10min", rotulo: "10 minutos", minutos: 10 },
  { id: "1h", rotulo: "1 hora", minutos: 60 },
  { id: "3h", rotulo: "3 horas", minutos: 180 },
  {
    id: "amanha",
    rotulo: "Amanha 9h",
    calcular: () => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
      return d;
    },
  },
  {
    id: "semana",
    rotulo: "Semana que vem",
    calcular: () => {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      d.setHours(9, 0, 0, 0);
      return d;
    },
  },
];

const LembretesPage = () => {
  const navigate = useNavigate();
  const { selectedWorkspaceId } = useWorkspace();
  const {
    abertos,
    resumo,
    carregando: carregandoAbertos,
    pendentesNaFila,
    recarregar,
    removerLocalmente,
    substituirLocalmente,
  } = useLembretes();

  const [aba, setAba] = useState("abertos");
  const [outrosLembretes, setOutrosLembretes] = useState([]);
  const [carregandoOutros, setCarregandoOutros] = useState(false);
  const [erro, setErro] = useState("");

  const [projetos, setProjetos] = useState([]);
  const [emConversao, setEmConversao] = useState(null);
  const [formConversao, setFormConversao] = useState({
    descricao: "",
    id_projeto: "",
    vencimento: "",
    prioridade: TASK_PRIORITY.MEDIUM,
  });
  const [convertendo, setConvertendo] = useState(false);

  const [emEdicao, setEmEdicao] = useState(null);
  const [formEdicao, setFormEdicao] = useState({
    texto: "",
    lembrarEm: "",
    recorrencia: REMINDER_RECURRENCE.NENHUMA,
  });
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);

  const [pushAtivo, setPushAtivo] = useState(false);
  const [mexendoNoPush, setMexendoNoPush] = useState(false);
  const [avisoPush, setAvisoPush] = useState("");
  const [avisoTom, setAvisoTom] = useState("warning");
  const [dispositivos, setDispositivos] = useState([]);
  const [endpointLocal, setEndpointLocal] = useState(null);
  const [motivoPush, setMotivoPush] = useState("");

  const ehAbaAberta = aba === "abertos";
  const lembretes = ehAbaAberta ? abertos : outrosLembretes;
  const carregando = ehAbaAberta ? carregandoAbertos : carregandoOutros;

  // Abas de historico buscam sob demanda; a de abertos vem do contexto, que ja
  // e compartilhado com o badge do header e o widget do dashboard.
  const carregarOutros = useCallback(async () => {
    if (ehAbaAberta) return;

    const status = ABAS.find((item) => item.id === aba)?.status;

    setCarregandoOutros(true);
    setErro("");

    try {
      setOutrosLembretes(await listarLembretes(status));
    } catch (error) {
      console.error("LembretesPage: erro ao carregar", error);
      setErro(error?.response?.data?.message || error?.message || "Erro ao carregar lembretes.");
      setOutrosLembretes([]);
    } finally {
      setCarregandoOutros(false);
    }
  }, [aba, ehAbaAberta]);

  useEffect(() => {
    carregarOutros();
  }, [carregarOutros]);

  useEffect(() => {
    garantirSincronizacaoAutomatica();
    sincronizarTokenComServiceWorker();
  }, []);

  useEffect(() => {
    api
      .get("/projects")
      .then((resposta) => setProjetos(resposta.data || []))
      .catch(() => setProjetos([]));
  }, []);

  /**
   * Estado real do push: inscricao presente NESTE navegador **e** registrada no
   * servidor. Olhar so o navegador era o que fazia a tela dizer "notificacoes
   * ativas" num Chrome que ja tinha perdido o registro e nao recebia nada.
   */
  const conferirPush = useCallback(async () => {
    if (!pushSuportado()) return;

    // O motivo da falha vira texto na tela: push que nao chega sem nenhuma
    // pista e o que torna esse problema tao caro de diagnosticar.
    const resultado = await garantirInscricaoSincronizada();
    setMotivoPush(resultado?.ok ? "" : resultado?.motivo || "");

    const [endpoint, registrados] = await Promise.all([
      endpointDesteAparelho(),
      listarDispositivos().catch(() => []),
    ]);

    setEndpointLocal(endpoint);
    setDispositivos(registrados);
    setPushAtivo(Boolean(endpoint) && registrados.some((item) => item.endpoint === endpoint));
  }, []);

  useEffect(() => {
    conferirPush();
  }, [conferirPush]);

  function aplicarAtualizacao(lembrete) {
    if (ehAbaAberta) substituirLocalmente(lembrete);
    else setOutrosLembretes((atual) => atual.map((i) => (i.id === lembrete.id ? lembrete : i)));
  }

  function aplicarRemocao(id) {
    if (ehAbaAberta) removerLocalmente(id);
    else setOutrosLembretes((atual) => atual.filter((i) => i.id !== id));
  }

  // ─── Acoes de triagem ───────────────────────────────────────────────────────

  async function aplicarStatus(lembrete, status) {
    setErro("");

    try {
      const atualizado = await alterarStatus(lembrete.id, status);

      // Concluir um recorrente nao encerra a serie: o servidor devolve a
      // proxima ocorrencia e o item continua aberto.
      if (atualizado && Number(atualizado.status) === REMINDER_STATUS.ABERTO) {
        aplicarAtualizacao(atualizado);
      } else {
        aplicarRemocao(lembrete.id);
      }
    } catch (error) {
      console.error("LembretesPage: erro ao alterar status", error);
      setErro(error?.response?.data?.message || "Nao foi possivel atualizar o lembrete.");
      recarregar();
    }
  }

  async function adiar(lembrete, opcao) {
    setErro("");

    try {
      const payload = opcao.calcular
        ? { lembrar_em: opcao.calcular().toISOString() }
        : { minutos: opcao.minutos };

      aplicarAtualizacao(await adiarLembrete(lembrete.id, payload));
    } catch (error) {
      console.error("LembretesPage: erro ao adiar", error);
      setErro(error?.response?.data?.message || "Nao foi possivel adiar o lembrete.");
      recarregar();
    }
  }

  async function excluir(lembrete) {
    setErro("");
    aplicarRemocao(lembrete.id);

    try {
      await removerLembrete(lembrete.id);
    } catch (error) {
      console.error("LembretesPage: erro ao excluir", error);
      setErro(error?.response?.data?.message || "Nao foi possivel excluir o lembrete.");
      recarregar();
      carregarOutros();
    }
  }

  // ─── Edicao ─────────────────────────────────────────────────────────────────

  function abrirEdicao(lembrete) {
    setFormEdicao({
      texto: lembrete.texto,
      lembrarEm: paraInputDateTime(lembrete.lembrar_em),
      recorrencia: Number(lembrete.recorrencia) || REMINDER_RECURRENCE.NENHUMA,
    });
    setEmEdicao(lembrete);
  }

  async function salvarEdicao() {
    if (!emEdicao) return;

    const texto = formEdicao.texto.trim();
    if (!texto) {
      setErro("O texto do lembrete nao pode ficar vazio.");
      return;
    }

    setSalvandoEdicao(true);
    setErro("");

    try {
      const atualizado = await atualizarLembrete(emEdicao.id, {
        texto,
        // datetime-local devolve hora local; o ISO carrega o fuso correto.
        lembrar_em: formEdicao.lembrarEm ? new Date(formEdicao.lembrarEm).toISOString() : null,
        // Sem horario a repeticao nao tem do que partir.
        recorrencia: formEdicao.lembrarEm
          ? formEdicao.recorrencia
          : REMINDER_RECURRENCE.NENHUMA,
      });

      aplicarAtualizacao(atualizado);
      setEmEdicao(null);
    } catch (error) {
      console.error("LembretesPage: erro ao salvar edicao", error);
      setErro(error?.response?.data?.message || "Nao foi possivel salvar o lembrete.");
    } finally {
      setSalvandoEdicao(false);
    }
  }

  // ─── Conversao em tarefa ────────────────────────────────────────────────────

  function abrirConversao(lembrete) {
    setFormConversao({
      descricao: lembrete.texto,
      id_projeto: "",
      // Um lembrete com hora ja sugere o vencimento da tarefa.
      vencimento: lembrete.lembrar_em ? String(lembrete.lembrar_em).slice(0, 10) : "",
      prioridade: TASK_PRIORITY.MEDIUM,
    });
    setEmConversao(lembrete);
  }

  async function confirmarConversao() {
    if (!emConversao) return;

    setConvertendo(true);
    setErro("");

    try {
      await converterEmTarefa(emConversao.id, {
        descricao: formConversao.descricao.trim() || emConversao.texto,
        // Sem id_workspace a tarefa nasce orfa e some das listas filtradas.
        id_workspace: selectedWorkspaceId || null,
        id_projeto: formConversao.id_projeto || null,
        vencimento: formConversao.vencimento || null,
        prioridade: formConversao.prioridade,
      });

      aplicarRemocao(emConversao.id);
      setEmConversao(null);
    } catch (error) {
      console.error("LembretesPage: erro ao converter", error);
      setErro(
        error?.response?.data?.message || error?.message || "Nao foi possivel converter em tarefa."
      );
    } finally {
      setConvertendo(false);
    }
  }

  // ─── Notificacoes ───────────────────────────────────────────────────────────

  async function alternarPush() {
    setMexendoNoPush(true);
    setAvisoPush("");

    try {
      if (pushAtivo) {
        await desativarNotificacoes();
      } else {
        await ativarNotificacoes();
      }

      // Reconferir com o servidor em vez de assumir: ativar so vale de verdade
      // quando o endpoint chegou no banco.
      await conferirPush();
    } catch (error) {
      setAvisoPush(error?.message || "Nao foi possivel alterar as notificacoes.");
    } finally {
      setMexendoNoPush(false);
    }
  }

  async function testar() {
    setAvisoPush("");

    try {
      const resultado = await enviarNotificacaoTeste();

      if (!resultado?.enviados) {
        setAvisoTom("warning");
        setAvisoPush("Nenhum aparelho recebeu. Ative as notificacoes neste navegador.");
      } else {
        // Separar "o servidor enviou" de "a notificacao apareceu" e o que
        // aponta o culpado: se enviou e nao apareceu, o bloqueio e do sistema.
        setAvisoTom("info");
        setAvisoPush(
          `Enviado para ${resultado.enviados} aparelho(s). Se nao apareceu neste aqui, o bloqueio e do sistema operacional — no Windows, veja Assistente de Foco e as notificacoes do Chrome.`
        );
      }

      await conferirPush();
    } catch (error) {
      setAvisoTom("warning");
      setAvisoPush(
        error?.response?.data?.message || error?.message || "Falha ao enviar a notificacao de teste."
      );
    }
  }

  const mostrarBlocoPush = pushSuportado() || precisaInstalarParaNotificar();
  const permissaoNegada = permissaoAtual() === "denied";

  /**
   * Por que este navegador nao esta recebendo. Sem esta frase, "ativei no
   * celular" e "estou registrado neste Chrome" pareciam a mesma coisa.
   */
  const diagnosticoPush = useMemo(() => {
    if (pushAtivo || permissaoNegada || precisaInstalarParaNotificar()) return null;

    if (motivoPush === "sem-permissao") {
      return "Este navegador ainda nao autorizou notificacoes. A permissao e por navegador: clique em Ativar aqui mesmo, no desktop.";
    }
    if (motivoPush === "sem-service-worker") {
      return "O service worker ainda nao esta ativo aqui. Recarregue a pagina e tente de novo.";
    }
    if (motivoPush === "erro") {
      return "Nao foi possivel registrar este navegador no servidor. Clique em Ativar para tentar de novo.";
    }
    if (endpointLocal) {
      return "Este navegador tem inscricao local, mas o servidor nao a reconhece. Clique em Ativar para registrar de novo.";
    }
    return null;
  }, [pushAtivo, permissaoNegada, motivoPush, endpointLocal]);
  const subtitulo = useMemo(() => {
    if (resumo.abertos === 0) return "Capture agora, decida depois o que vira tarefa.";
    return `${resumo.abertos} aberto(s)${
      resumo.vencidos > 0 ? ` · ${resumo.vencidos} vencido(s)` : ""
    }`;
  }, [resumo]);

  return (
    <div className="lembretes-page">
      <header className="lembretes-topo">
        <div>
          <h4>
            <FontAwesomeIcon icon={faInbox} /> Lembretes
          </h4>
          <small>{subtitulo}</small>
        </div>

        <Button className="lembretes-novo" onClick={() => navigate("/capturar")}>
          <FontAwesomeIcon icon={faPlus} /> Capturar
        </Button>
      </header>

      {mostrarBlocoPush && (
        <div className="lembretes-push">
          <div className="lembretes-push-info">
            <FontAwesomeIcon icon={pushAtivo ? faBell : faBellSlash} />
            <div>
              <strong>
                {pushAtivo ? "Notificacoes ativas neste aparelho" : "Notificacoes desligadas"}
              </strong>
              <span>
                {precisaInstalarParaNotificar()
                  ? "No iPhone: Safari → Compartilhar → Adicionar a Tela de Inicio."
                  : permissaoNegada
                  ? "A permissao foi bloqueada. Libere nas configuracoes do navegador."
                  : pushAtivo
                  ? "Cada navegador e um aparelho separado: ative em todos onde quiser ser avisado."
                  : "Sem elas o lembrete nao toca no horario marcado."}
              </span>
            </div>
          </div>

          <div className="lembretes-push-acoes">
            {pushAtivo && (
              <Button size="sm" variant="outline-secondary" onClick={testar}>
                Testar
              </Button>
            )}
            <Button
              size="sm"
              variant={pushAtivo ? "outline-danger" : "primary"}
              onClick={alternarPush}
              disabled={mexendoNoPush || precisaInstalarParaNotificar() || permissaoNegada}
            >
              {mexendoNoPush ? "..." : pushAtivo ? "Desligar" : "Ativar"}
            </Button>
          </div>
        </div>
      )}

      {/* A lista existe porque o sintoma "so o celular toca" e invisivel sem
          ela: o push e por navegador, e ate aqui nao havia como ver quais
          aparelhos o servidor conhece. */}
      {mostrarBlocoPush && dispositivos.length > 0 && (
        <div className="lembretes-dispositivos">
          <strong>Aparelhos que recebem notificacao</strong>
          <ul>
            {dispositivos.map((dispositivo) => (
              <li key={dispositivo.id}>
                <FontAwesomeIcon icon={faBell} />
                <span>{dispositivo.descricao}</span>
                {dispositivo.endpoint === endpointLocal && <em>este aparelho</em>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {diagnosticoPush && (
        <Alert variant="warning" className="mt-2">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {diagnosticoPush}
        </Alert>
      )}

      {avisoPush && <Alert variant={avisoTom} className="mt-2">{avisoPush}</Alert>}

      {pendentesNaFila > 0 && (
        <Alert variant="info" className="lembretes-fila">
          <FontAwesomeIcon icon={faCloudArrowUp} /> {pendentesNaFila} lembrete(s) capturado(s) offline,
          aguardando envio.
        </Alert>
      )}

      <nav className="lembretes-abas">
        {ABAS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`lembretes-aba ${aba === item.id ? "ativa" : ""}`}
            onClick={() => setAba(item.id)}
          >
            {item.rotulo}
            {item.id === "abertos" && resumo.abertos > 0 && ` (${resumo.abertos})`}
          </button>
        ))}
      </nav>

      {erro && <Alert variant="danger">{erro}</Alert>}

      {carregando && lembretes.length === 0 ? (
        <div className="lembretes-carregando">
          <Spinner size="sm" />
        </div>
      ) : lembretes.length === 0 ? (
        <div className="lembretes-vazio">
          <FontAwesomeIcon icon={faCheck} />
          <strong>Nada por aqui.</strong>
          <span>
            {ehAbaAberta ? "Caixa de entrada limpa." : "Nenhum lembrete nesta aba ainda."}
          </span>
        </div>
      ) : (
        <div className="lembretes-lista">
          {lembretes.map((lembrete) => {
            const vencido =
              Number(lembrete.status) === REMINDER_STATUS.ABERTO &&
              estaVencido(lembrete.lembrar_em);
            const recorrente = ehRecorrente(lembrete.recorrencia);

            return (
              <article key={lembrete.id} className={`lembrete-item ${vencido ? "vencido" : ""}`}>
                <div className="lembrete-conteudo">
                  <strong>{lembrete.texto}</strong>

                  <div className="lembrete-meta">
                    {lembrete.lembrar_em ? (
                      <span className={`lembrete-hora ${vencido ? "vencida" : ""}`}>
                        <FontAwesomeIcon icon={vencido ? faTriangleExclamation : faClock} />{" "}
                        {formatarLembrarEm(lembrete.lembrar_em)}
                      </span>
                    ) : (
                      <span className="lembrete-hora sem-hora">Sem alarme</span>
                    )}

                    {recorrente && (
                      <span className="lembrete-tag recorrente">
                        <FontAwesomeIcon icon={faRepeat} />{" "}
                        {getRecurrenceLabel(lembrete.recorrencia)}
                      </span>
                    )}

                    {lembrete.id_tarefa && (
                      <span className="lembrete-tag">virou tarefa #{lembrete.id_tarefa}</span>
                    )}
                  </div>
                </div>

                {Number(lembrete.status) === REMINDER_STATUS.ABERTO ? (
                  <div className="lembrete-acoes">
                    <button
                      type="button"
                      className="lembrete-acao feito"
                      title={recorrente ? "Feito (vai para a proxima ocorrencia)" : "Marcar como feito"}
                      aria-label="Marcar como feito"
                      onClick={() => aplicarStatus(lembrete, REMINDER_STATUS.FEITO)}
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </button>

                    <button
                      type="button"
                      className="lembrete-acao editar"
                      title="Editar"
                      aria-label="Editar lembrete"
                      onClick={() => abrirEdicao(lembrete)}
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>

                    <Dropdown align="end">
                      <Dropdown.Toggle
                        as="button"
                        type="button"
                        className="lembrete-acao adiar"
                        title="Adiar"
                        aria-label="Adiar lembrete"
                      >
                        <FontAwesomeIcon icon={faSnowflake} />
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Header>Adiar para</Dropdown.Header>
                        {OPCOES_ADIAR.map((opcao) => (
                          <Dropdown.Item key={opcao.id} onClick={() => adiar(lembrete, opcao)}>
                            {opcao.rotulo}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>

                    <button
                      type="button"
                      className="lembrete-acao converter"
                      title="Transformar em tarefa"
                      aria-label="Transformar em tarefa"
                      onClick={() => abrirConversao(lembrete)}
                    >
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>

                    <button
                      type="button"
                      className="lembrete-acao descartar"
                      title="Descartar"
                      aria-label="Descartar"
                      onClick={() => aplicarStatus(lembrete, REMINDER_STATUS.DESCARTADO)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                ) : (
                  <div className="lembrete-acoes">
                    <button
                      type="button"
                      className="lembrete-acao descartar"
                      title="Excluir definitivamente"
                      aria-label="Excluir definitivamente"
                      onClick={() => excluir(lembrete)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {/* ─── Edicao ──────────────────────────────────────────────────────────── */}

      <Modal show={Boolean(emEdicao)} onHide={() => setEmEdicao(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar lembrete</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Texto</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formEdicao.texto}
              onChange={(evento) =>
                setFormEdicao((atual) => ({ ...atual, texto: evento.target.value }))
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Quando avisar</Form.Label>
            <div className="lembrete-edicao-data">
              <Form.Control
                type="datetime-local"
                value={formEdicao.lembrarEm}
                onChange={(evento) =>
                  setFormEdicao((atual) => ({ ...atual, lembrarEm: evento.target.value }))
                }
              />
              {formEdicao.lembrarEm && (
                <Button
                  variant="outline-secondary"
                  onClick={() =>
                    setFormEdicao((atual) => ({
                      ...atual,
                      lembrarEm: "",
                      recorrencia: REMINDER_RECURRENCE.NENHUMA,
                    }))
                  }
                >
                  Sem alarme
                </Button>
              )}
            </div>
            <Form.Text muted>
              Mexer no horario rearma a notificacao, mesmo se ela ja tinha disparado.
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>Repetir</Form.Label>
            <Form.Select
              value={formEdicao.recorrencia}
              disabled={!formEdicao.lembrarEm}
              onChange={(evento) =>
                setFormEdicao((atual) => ({
                  ...atual,
                  recorrencia: Number(evento.target.value),
                }))
              }
            >
              {RECURRENCE_OPTIONS.map((opcao) => (
                <option key={opcao} value={opcao}>
                  {getRecurrenceLabel(opcao)}
                </option>
              ))}
            </Form.Select>
            <Form.Text muted>
              {formEdicao.lembrarEm
                ? "Ao concluir, o lembrete pula para a proxima ocorrencia em vez de encerrar."
                : "Defina um horario para poder repetir."}
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEmEdicao(null)} disabled={salvandoEdicao}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={salvarEdicao} disabled={salvandoEdicao}>
            {salvandoEdicao ? "Salvando..." : "Salvar"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── Conversao ───────────────────────────────────────────────────────── */}

      <Modal show={Boolean(emConversao)} onHide={() => setEmConversao(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Transformar em tarefa</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {emConversao && ehRecorrente(emConversao.recorrencia) && (
            <Alert variant="warning" className="py-2">
              Este lembrete se repete ({getRecurrenceLabel(emConversao.recorrencia)}). Ao virar
              tarefa, a repeticao termina.
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Descricao</Form.Label>
            <Form.Control
              type="text"
              value={formConversao.descricao}
              onChange={(evento) =>
                setFormConversao((atual) => ({ ...atual, descricao: evento.target.value }))
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Projeto</Form.Label>
            <Form.Select
              value={formConversao.id_projeto}
              onChange={(evento) =>
                setFormConversao((atual) => ({ ...atual, id_projeto: evento.target.value }))
              }
            >
              <option value="">Sem projeto</option>
              {projetos.map((projeto) => (
                <option key={projeto.id} value={projeto.id}>
                  {projeto.descricao || projeto.nome || projeto.titulo || `Projeto ${projeto.id}`}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Vencimento</Form.Label>
            <Form.Control
              type="date"
              value={formConversao.vencimento}
              onChange={(evento) =>
                setFormConversao((atual) => ({ ...atual, vencimento: evento.target.value }))
              }
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Prioridade</Form.Label>
            <Form.Select
              value={formConversao.prioridade}
              onChange={(evento) =>
                setFormConversao((atual) => ({
                  ...atual,
                  prioridade: Number(evento.target.value),
                }))
              }
            >
              {PRIORIDADES.map((prioridade) => (
                <option key={prioridade} value={prioridade}>
                  {getTaskPriorityLabel(prioridade)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEmConversao(null)} disabled={convertendo}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={confirmarConversao} disabled={convertendo}>
            {convertendo ? "Criando..." : "Criar tarefa"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LembretesPage;
